// Os rótulos das superfícies de viagem, num lugar só. Três telas escrevem as
// MESMAS frases sobre os mesmos dados — o card da home, o painel da viagem
// (itinerário compacto) e o card do voucher (detalhe completo) —, e frase
// duplicada divergiria no primeiro ajuste de cópia.
//
// A maior parte deste vocabulário morou aqui e se mudou para
// `@voll-intelligence/trip-document` (Task 6): os rótulos abaixo que delegam a
// `doc.xxx` são adaptadores finos — amarram o `t()`/locale do web-chat ao `ctx`
// que o pacote pede, com a assinatura VELHA (sem `ctx`) para não mexer em
// nenhum chamador. O que sobra 100% local é o que o pacote não conhece: a pill
// de proximidade da home, a grade de fatos do painel (`serviceFacts`), e o
// pedido de recotação da seção "Precisa de sua atenção".
import * as doc from '@voll-intelligence/trip-document';
import { t } from './i18n.js';
import { hasAirlineMark } from './airline_logos.js';
import {
  calendarDateFromUtcDay,
  monthShort,
  type ModalKind,
  type ServiceLeg,
  type TripPillModel,
  type TripService,
  type TripStop,
} from './derive.js';
import { documentCtx } from './document_ctx.js';

/** O nome do modal na palavra de MERCADO ("Transfer", "Carro") — a mesma que os tiles de
 *  acrescentar serviço e o calendário já usam. Não confundir com `modalLabel`, do
 *  `voucher_meta`, que fala o vocabulário da HOME ("Mobilidade", "Carros"): são duas
 *  famílias de cópia vivas, e a desta é a das superfícies de viagem.
 *
 *  Reexportado do pacote: todo chamador passa o resultado direto para `t()`. */
export const MODAL_LABEL_KEY: Record<ModalKind, string> = doc.MODAL_LABEL_KEY;

/** O rótulo da pill de proximidade da viagem ("Em 6 dias" / "Hoje" / "Iniciada").
 *  Só `inDays` interpola contagem — daí o desvio. */
export function tripPillLabel(pill: TripPillModel): string {
  if (pill.labelKey === 'trips.pill.inDays') return t('trips.pill.inDays', { count: pill.days });
  return t(pill.labelKey);
}

/** "Seg 3 jun" — o cabeçalho de um dia do itinerário. */
export function dayHeading(date: Date): string {
  return doc.dayHeading(date, documentCtx().locale);
}

/** "2 nov" (dia único) / "4–5 nov" / "29 jun – 2 jul" — a faixa de uma estadia no
 *  relógio de parede de quem lê (as datas já são meia-noite LOCAL). É a mesma
 *  conta do carrossel (`formatTripRange`), não a de `periodLabel`, que repetiria
 *  o mês ("2 nov – 5 nov"). */
export function stayRange(from: Date, to: Date | null): string {
  return doc.formatTripRange(from, to, documentCtx().locale);
}

/** "15 jul" de uma data-só armazenada à meia-noite UTC. */
export function dayLabel(date: Date | null): string | null {
  const local = calendarDateFromUtcDay(date);
  return local ? `${String(local.getDate())} ${monthShort(local)}` : null;
}

/** "15 jul" de uma data que JÁ está no relógio de parede de quem lê — a partida
 *  de um trecho (`ServiceLeg.departAt` vem de `departLocal`) ou um dia de
 *  calendário já convertido. É o irmão de `dayLabel`, e a diferença não é
 *  cosmética: `dayLabel` lê os componentes UTC, então um voo das 21:35 em
 *  São Paulo viraria "o dia seguinte", e uma data já convertida seria convertida
 *  DE NOVO (errando fora de UTC−3). */
export function localDayLabel(date: Date | null): string | null {
  return date ? `${String(date.getDate())} ${monthShort(date)}` : null;
}

/** Quem identifica o serviço: o hotel pelo NOME e o carro pelo MODELO — é o que a pessoa
 *  reconhece, e não o balcão que entrega. Os demais caem na regra compartilhada
 *  (fornecedor, senão o rótulo do modal). */
export function serviceHeading(service: TripService): string {
  return doc.serviceHeading(service, documentCtx());
}

/** O rótulo de UMA linha do detalhamento de custo (o popover da régua de meta): quem
 *  identifica o serviço e, no aéreo, a ROTA — "Latam · GRU → CGH". A companhia
 *  sozinha não diz QUAL voo custou aquilo numa viagem com dois, e a linha do hotel ao
 *  lado (que traz o nome do hotel) fica na mesma régua. A rota é a da IDA: num
 *  ida-e-volta a última perna volta ao ponto de partida, e "GRU → GRU" não é rota.
 *  Sem as duas pontas na fonte sobra a companhia, como antes — meia flecha diz menos
 *  que nada (mesma régua do `routeLabel`). */
export function budgetLineLabel(service: TripService): string {
  const head = serviceHeading(service);
  if (service.detail?.kind !== 'FLIGHT') return head;
  const legs = service.detail.legs;
  const outward = legs.filter((l) => l.leg !== 'volta');
  const route = doc.routeLabel(outward.length > 0 ? outwardEnds(outward) : outwardEnds(legs));
  return route ? `${head} \u00b7 ${route}` : head;
}

/** As duas pontas de uma sequência de pernas: parte da primeira, chega na última — a
 *  rota do multi-trecho é o percurso inteiro, não o primeiro salto. */
function outwardEnds(legs: readonly ServiceLeg[]): Pick<ServiceLeg, 'from' | 'to'> {
  return { from: legs[0]?.from ?? null, to: legs.at(-1)?.to ?? null };
}

/** A bagagem DESPACHADA do trecho: a franquia que a tarifa declara (peças/peso), senão a
 *  franquia da fonte com a unidade que ela esquece, a prosa dela quando é prosa, senão a
 *  frase que o booleano merece. `null` quando ninguém sabe. */
export function baggageLabel(
  leg: Pick<ServiceLeg, 'baggage' | 'checkedBaggage' | 'baggageAllowance'>,
): string | null {
  return doc.baggageLabel(leg, documentCtx());
}

/** A bagagem que o BILHETE afirma no trecho, por extenso — o chip do card do voo no
 *  itinerário e o do cartão vivo. Afirma quem dá franquia: o campo cru da fonte ou a franquia
 *  que a tarifa declara, cada um pela régua do domínio — `'0'`, `'undefined'` e vazio são "não
 *  informado", e o "0" pelado no chip calava a oferta de bagagem que o assento ao lado faz
 *  (#4586). `null` deixa a lacuna aberta, e também quando a tarifa NEGA o despacho: sem
 *  bagagem despachada é justamente quando a oferta "Adicionar" mais serve. O `checkedBaggage`
 *  sozinho não diz franquia nenhuma — só dá o texto. */
export function statedBaggageLabel(
  leg: Pick<ServiceLeg, 'baggage' | 'checkedBaggage' | 'baggageAllowance'>,
): string | null {
  if (leg.baggageAllowance?.checked?.included === false) return null;
  const ctx = documentCtx();
  const stated =
    doc.baggageLabel({ baggage: leg.baggage }, ctx) ??
    doc.baggageLabel({ baggageAllowance: leg.baggageAllowance }, ctx);
  return stated === null ? null : doc.baggageLabel(leg, ctx);
}

/** "direto" / "1 parada"; null quando a fonte não informa paradas. Recebe o número
 *  cru, e não o trecho, porque as superfícies guardam esse dado em formas
 *  diferentes (o trecho do voucher, o voo em destaque) e a frase é a mesma. */
export function stopsLabel(stops: number | null | undefined): string | null {
  return doc.stopsLabel(stops, documentCtx());
}

/** A cabine, quando o que veio É cabine — e traduzida: valor cru da fonte dentro
 *  de uma frase montada por `t()` não passa por i18n nenhum. */
export function cabinLabel(travelClass: string | null): string | null {
  return doc.cabinLabel(travelClass, documentCtx());
}

/** O que nomeia UM passo do itinerário: a PERNA ("Voo de ida"), como no protótipo;
 *  o resto pelo serviço (nome do hotel, fornecedor). A marca que decide se o número
 *  do voo aparece SOZINHO ("1769") ou com a companhia ("GOL 1769") é a do painel —
 *  `hasAirlineMark`, a mesma régua do thumb — e não a do pacote, que não sabe
 *  desenhar nada. */
export function stopTitle(stop: TripStop): { name: string; flightNo: string | null } {
  return doc.stopTitle(stop, documentCtx(), hasAirlineMark);
}

/** A linha de detalhe do passo: rota + paradas + cabine no trecho; a estada nos
 *  modais sem trecho. */
export function stopDetail(stop: TripStop): string | null {
  return doc.stopDetail(stop, documentCtx());
}

/** O rótulo de quem abre o voucher é a linha INTEIRA, não só o título: numa viagem
 *  multi-trecho os três passos se chamam "Voo de ida" (a fonte tipa cada voucher de
 *  perna única como OUTWARD), e quem os distingue na tela é o detalhe — a rota. Sem
 *  ele, quem usa leitor de tela ouviria o mesmo texto em vouchers diferentes.
 *  Compartilhado entre o card do passo e a tarja "A seguir": os dois abrem o MESMO
 *  documento e têm de se anunciar igual. */
export function stopOpenLabel(stop: TripStop): string {
  const { name, flightNo } = stopTitle(stop);
  return [name, flightNo, stopDetail(stop)].filter(Boolean).join(' · ');
}

/** Um passo numa linha de TEXTO puro — para superfícies que não desenham logo
 *  nenhuma (o trilho "Reservando nesta viagem" da bancada de hotel, #3022). Não é
 *  `stopOpenLabel`: aquele descreve o card do painel, que DESENHA a marca, e por
 *  isso pede o `stopTitle` do painel, que tira o nome da companhia quando há logo.
 *  Aqui a marca nunca existe, então o título vai SEM ela — o nome da companhia
 *  fica ("GOL 1769/7748"), senão o número sai órfão ao lado de uma Copa que, sem
 *  logo curada, manteve o nome. No trecho, o detalhe é SÓ a rota — o único
 *  desempate entre três "Voo de ida" de perna única; paradas e cabine são ruído num
 *  resumo de um dia por linha. Fora do trecho (hotel, carro), a linha de detalhe
 *  de sempre. */
export function stopTextLine(stop: TripStop): string {
  const { name, flightNo } = doc.stopTitle(stop, documentCtx());
  const detail = stop.leg ? doc.routeLabel(stop.leg) : stopDetail(stop);
  return [name, flightNo, detail].filter(Boolean).join(' · ');
}
